import { createFileRoute, Link } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { useState } from "react";
import { Pager } from "@/components/pager";
import type { Database } from "@/integrations/supabase/types";
import { SubPage } from "@/components/sub-page";

type NoticiaListItem = {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  category: string | null;
  published_at: string | null;
  og_image_url: string | null;
};

const listNoticias = createServerFn({ method: "GET" }).handler(async () => {
  const supa = createClient<Database>(
    process.env.SUPABASE_URL!,
    process.env.SUPABASE_PUBLISHABLE_KEY!,
    { auth: { storage: undefined, persistSession: false, autoRefreshToken: false } },
  );
  const { data, error } = await supa
    .from("noticias")
    .select("id,slug,title,excerpt,category,published_at,og_image_url")
    .eq("is_published", true)
    .order("sort_order", { ascending: true })
    .order("published_at", { ascending: false, nullsFirst: false })
    .limit(50);
  if (error) return [] as NoticiaListItem[];
  return (data ?? []) as NoticiaListItem[];
});

export const Route = createFileRoute("/comunicaciones/noticias/")({
  loader: () => listNoticias(),
  head: () => ({
    meta: [
      { title: "Noticias | Mine Class" },
      { name: "description", content: "Noticias, análisis y actualidad de la industria minera: capacitación, seguridad, acreditación y transformación digital por Mine Class." },
      { property: "og:title", content: "Noticias Mine Class" },
      { property: "og:description", content: "Cobertura editorial de Mine Class sobre capacitación, seguridad, acreditación digital y minería 4.0." },
      { property: "og:url", content: "/comunicaciones/noticias" },
    ],
    links: [{ rel: "canonical", href: "/comunicaciones/noticias" }],
  }),
  errorComponent: () => (
    <SubPage section="comunicaciones" eyebrow="Comunicaciones" title="Noticias" />
  ),
  component: Page,
});

function Page() {
  const items = Route.useLoaderData() as NoticiaListItem[];
  const perPage = 6;
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(items.length / perPage));
  const current = items.slice((page - 1) * perPage, page * perPage);
  const goto = (p: number) => {
    setPage(p);
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  };
  return (
    <SubPage
      section="comunicaciones"
      eyebrow="Comunicaciones"
      title="Noticias"
      intro="Actualidad, comunicaciones internas y contenidos relevantes."
    >
      {items.length === 0 ? (
        <p className="text-[var(--color-on-surface-variant)]">Aún no hay noticias publicadas.</p>
      ) : (
        <>
          <div className="grid md:grid-cols-2 gap-6 not-prose">
            {current.map((n) => (
              <Link
                key={n.id}
                to="/comunicaciones/noticias/$slug"
                params={{ slug: n.slug }}
                className="bg-white border border-[var(--color-outline-variant)] hover:border-[var(--color-brand-coral)] transition-colors p-6 block"
              >
                {n.og_image_url && (
                  <img src={n.og_image_url} alt="" className="w-full h-48 object-cover mb-4" />
                )}
                {n.category && (
                  <span className="text-xs uppercase tracking-widest text-[var(--color-brand-coral)] font-bold">
                    {n.category}
                  </span>
                )}
                <h3 className="text-xl font-bold mt-2 text-[var(--color-brand-purple)]">{n.title}</h3>
                {n.excerpt && (
                  <p className="text-sm text-[var(--color-on-surface-variant)] mt-2 line-clamp-3">{n.excerpt}</p>
                )}
                {n.published_at && (
                  <p className="text-xs text-[var(--color-on-surface-variant)] mt-3">
                    {new Date(n.published_at).toLocaleDateString("es-CL", { dateStyle: "long" })}
                  </p>
                )}
              </Link>
            ))}
          </div>
          <Pager page={page} totalPages={totalPages} onChange={goto} />
        </>
      )}
    </SubPage>
  );
}
